/**
 * Cookie Settings Modal Module
 * Displays detailed cookie settings interface
 */
import type { CookieTypes } from '../types';
import type { CookieManager } from '../services/cookieManager';

export class CookieSettingsModal {
    cookieManager: CookieManager;
    modal: HTMLElement;
    isVisible: boolean;
    settings: Record<string, boolean>;
    fromBanner: boolean;

    constructor(cookieManager: CookieManager) {
        this.cookieManager = cookieManager;
        this.modal = document.createElement('div');
        this.isVisible = false;
        this.settings = {};
        this.fromBanner = false;
        
        this.createModal();
        this.bindEvents();
    }

    /**
     * Create the cookie settings modal HTML
     */
    createModal(): void {
        this.modal.className = 'cookie-settings-modal';
        this.modal.id = 'cookieSettingsModal';
        
        this.modal.innerHTML = `
            <div class="cookie-modal-overlay"></div>
            <div class="cookie-modal-container">
                <div class="cookie-modal-header">
                    <div class="cookie-modal-header-text">
                        <h2 class="cookie-modal-title">Cookie Settings</h2>
                        <p class="cookie-modal-subtitle">Manage your cookie preferences</p>
                    </div>
                </div>
                
                <div class="cookie-categories">
                    ${this.generateCategoriesHTML()}
                </div>
                
                <div class="cookie-modal-actions">
                    <button class="cookie-reject-all-btn" id="cookieRejectAllBtn">
                        Reject All
                    </button>
                    <div class="cookie-action-group">
                        <button class="cookie-save-settings-btn" id="cookieSaveSettingsBtn">
                            Save Settings
                        </button>
                        <button class="cookie-accept-all-btn" id="cookieAcceptAllBtn">
                            Accept All
                        </button>
                    </div>
                </div>
            </div>
        `;

        // Add modal to document body
        document.body.appendChild(this.modal);
    }

    /**
     * Generate HTML for cookie categories
     */
    generateCategoriesHTML(): string {
        const cookieTypes = this.cookieManager.getCookieTypes();
        let html = '';

        Object.keys(cookieTypes).forEach(key => {
            const cookie = cookieTypes[key as keyof CookieTypes];
            const isDisabled = cookie.required;
            const isChecked = cookie.enabled;
            
            html += `
                <div class="cookie-category ${isDisabled ? 'cookie-category-disabled' : ''}">
                    <div class="cookie-category-header">
                        <div class="cookie-category-info">
                            <h3 class="cookie-category-name">${cookie.name}</h3>
                            <p class="cookie-category-description">${cookie.description}</p>
                        </div>
                        <div class="cookie-toggle ${isChecked ? 'cookie-toggle-active' : 'cookie-toggle-inactive'} ${isDisabled ? 'cookie-toggle-disabled' : ''}" data-cookie-type="${key}">
                            <div class="cookie-toggle-button"></div>
                        </div>
                    </div>
                    <p class="cookie-category-details">${cookie.details}</p>
                </div>
            `;
        });

        return html;
    }

    /**
     * Bind event listeners
     */
    bindEvents(): void {
        // Modal overlay click to close (but we don't want this according to requirements)
        // const overlay = this.modal.querySelector('.cookie-modal-overlay');
        // overlay.addEventListener('click', () => this.hide());

        // Button event listeners
        const rejectAllBtn = this.modal.querySelector('#cookieRejectAllBtn');
        const saveSettingsBtn = this.modal.querySelector('#cookieSaveSettingsBtn');
        const acceptAllBtn = this.modal.querySelector('#cookieAcceptAllBtn');

        rejectAllBtn?.addEventListener('click', () => this.handleRejectAll());
        saveSettingsBtn?.addEventListener('click', () => this.handleSaveSettings());
        acceptAllBtn?.addEventListener('click', () => this.handleAcceptAll());

        // Toggle event listeners
        this.bindToggleEvents();

        // Listen for show cookie settings event
        window.addEventListener('showCookieSettings', (event: Event) => {
            const customEvent = event as CustomEvent<{ fromBanner: boolean }>;
            const fromBanner = customEvent.detail?.fromBanner || false;
            this.show(fromBanner);
        });
    }

    /**
     * Bind toggle switch events
     */
    bindToggleEvents(): void {
        const toggles = this.modal.querySelectorAll('.cookie-toggle:not(.cookie-toggle-disabled)');
        
        toggles.forEach(toggle => {
            toggle.addEventListener('click', () => {
                const cookieType = (toggle as HTMLElement).dataset.cookieType;
                if (!cookieType) return;
                
                const isActive = toggle.classList.contains('cookie-toggle-active');
                
                if (isActive) {
                    toggle.classList.remove('cookie-toggle-active');
                    toggle.classList.add('cookie-toggle-inactive');
                    this.settings[cookieType] = false;
                } else {
                    toggle.classList.remove('cookie-toggle-inactive');
                    toggle.classList.add('cookie-toggle-active');
                    this.settings[cookieType] = true;
                }
            });
        });
    }

    /**
     * Initialize settings from current cookie manager state
     */
    initializeSettings(): void {
        const cookieTypes = this.cookieManager.getCookieTypes();
        this.settings = {};
        
        Object.keys(cookieTypes).forEach(key => {
            const cookie = cookieTypes[key as keyof CookieTypes];
            if (!cookie.required) {
                // Use the default enabled state from cookie types
                this.settings[key] = cookie.enabled;
            }
        });
    }

    /**
     * Update toggle states based on current settings
     */
    updateToggleStates(): void {
        const cookieTypes = this.cookieManager.getCookieTypes();
        
        Object.keys(cookieTypes).forEach(key => {
            const toggle = this.modal.querySelector(`[data-cookie-type="${key}"]`);
            const cookie = cookieTypes[key as keyof CookieTypes];
            
            if (toggle && !cookie.required) {
                const isEnabled = this.settings[key] || cookie.enabled;
                
                if (isEnabled) {
                    toggle.classList.remove('cookie-toggle-inactive');
                    toggle.classList.add('cookie-toggle-active');
                } else {
                    toggle.classList.remove('cookie-toggle-active');
                    toggle.classList.add('cookie-toggle-inactive');
                }
            }
        });
    }

    /**
     * Handle reject all button click
     */
    handleRejectAll(): void {
        this.cookieManager.rejectAll();
        this.hide();
        // Don't hide banner if we came from banner, as it's already hidden
        if (!this.fromBanner) {
            this.hideBanner();
        }
    }

    /**
     * Handle save settings button click
     */
    handleSaveSettings(): void {
        this.cookieManager.saveSettings({
            analytics: this.settings['analytics'] || false,
            marketing: this.settings['marketing'] || false
        });
        this.hide();
        // Don't hide banner if we came from banner, as it's already hidden
        if (!this.fromBanner) {
            this.hideBanner();
        }
    }

    /**
     * Handle accept all button click
     */
    handleAcceptAll(): void {
        this.cookieManager.acceptAll();
        this.hide();
        // Don't hide banner if we came from banner, as it's already hidden
        if (!this.fromBanner) {
            this.hideBanner();
        }
    }

    /**
     * Hide the cookie banner after settings are saved
     */
    hideBanner(): void {
        // Dispatch event to hide cookie banner
        window.dispatchEvent(new CustomEvent('hideCookieBanner'));
    }

    /**
     * Show the modal
     */
    show(fromBanner: boolean = false): void {
        this.fromBanner = fromBanner;
        this.initializeSettings();
        this.updateToggleStates();
        this.modal.classList.add('cookie-modal-visible');
        this.isVisible = true;
        
        // Prevent scrolling on body
        document.body.classList.add('cookie-modal-active');
    }

    /**
     * Hide the modal
     */
    hide(): void {
        this.modal.classList.remove('cookie-modal-visible');
        this.isVisible = false;
        
        // Restore scrolling on body
        document.body.classList.remove('cookie-modal-active');
    }

    /**
     * Destroy the modal
     */
    destroy(): void {
        if (this.modal && this.modal.parentNode) {
            this.modal.parentNode.removeChild(this.modal);
        }
        this.isVisible = false;
        document.body.classList.remove('cookie-modal-active');
    }
}
